/**
 * E06-F05-T07 — the disclosure behind a score percentage.
 *
 * The bug: the long-list showed a naked "5%" / "0%" / "8%" with no way to learn what
 * it is made of, and no answer to "why is a 5% company on my list at all". This
 * component turns the number into a button that opens a full breakdown row:
 *
 *   - the weighted factors (criterion · weight · verdict · points earned), which add
 *     up to exactly the percentage on screen;
 *   - an explicit "not proven ≠ does not match" note for every criterion the AI check
 *     could not cite verbatim on the company site (those score 0 — the real reason
 *     live rows show single-digit percentages);
 *   - WHERE the row came from: the free discovery stage (before anything is scored)
 *     or a manual add — i.e. the long-list is never pre-filtered by percentage.
 *
 * Rendering shape: a `<tr>` panel spanning the table, NOT an absolutely-positioned
 * popover — both tables live inside `overflow-hidden` scroll areas that would clip a
 * floating card. One panel is open at a time; the toggle is a real button
 * (aria-expanded / aria-controls), so it is keyboard- and screen-reader-accessible.
 */
import type { JSX } from 'react';
import type { ScoreExplanation, ScoreFactor } from '@eer/shared';

// ---------------------------------------------------------------------------
// ids + palette
// ---------------------------------------------------------------------------

/** DOM id of the panel a toggle controls (aria-controls ↔ panel id). */
export function scoreExplainPanelId(rowId: number): string {
  return `score-explain-panel-${rowId}`;
}

const VERDICT_STYLE: Record<ScoreFactor['verdict'], { label: string; className: string }> = {
  pass: { label: 'confirmed', className: 'bg-green-100 text-green-700' },
  partial: { label: 'partial', className: 'bg-amber-100 text-amber-700' },
  fail: { label: 'does not match', className: 'bg-red-100 text-red-600' },
  unclear: { label: 'not proven', className: 'bg-slate-100 text-slate-500' },
};

// ---------------------------------------------------------------------------
// the toggle (sits in the score cell, next to the number)
// ---------------------------------------------------------------------------

export interface ScoreExplainToggleProps {
  readonly explanation: ScoreExplanation;
  readonly rowId: number;
  /** What the button shows (the percentage / the whole score cell). */
  readonly children: React.ReactNode;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly className?: string;
  /** E07-F04-T21 — the click opens the Evidence drawer (a dialog), not an inline row. */
  readonly opensDialog?: boolean;
}

/**
 * The percentage itself becomes the affordance: clicking it opens the breakdown.
 * `title` carries the one-line headline so a plain hover already answers "what is
 * this number" without a click.
 */
export function ScoreExplainToggle({
  explanation,
  rowId,
  children,
  open,
  onToggle,
  className,
  opensDialog = false,
}: ScoreExplainToggleProps): JSX.Element {
  return (
    <button
      type="button"
      data-testid={`score-explain-toggle-${rowId}`}
      aria-haspopup={opensDialog ? 'dialog' : undefined}
      aria-expanded={open}
      aria-controls={scoreExplainPanelId(rowId)}
      title={`${explanation.headline} Click for the full breakdown.`}
      onClick={onToggle}
      className={`inline-flex items-center gap-1 rounded px-1 text-left hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-400 ${
        className ?? ''
      }`}
    >
      {children}
      <i
        className={`fa-solid fa-circle-info text-[10px] ${open ? 'text-[#D4AF37]' : 'text-slate-400'}`}
        aria-hidden
      />
      <span className="sr-only">How is this score calculated?</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// the panel body (reusable outside a table)
// ---------------------------------------------------------------------------

export function ScoreExplainPanel({
  explanation,
  rowId,
  onClose,
}: {
  readonly explanation: ScoreExplanation;
  readonly rowId: number;
  readonly onClose?: () => void;
}): JSX.Element {
  return (
    <div
      id={scoreExplainPanelId(rowId)}
      data-testid={`score-explain-body-${rowId}`}
      className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-[13px] text-slate-700"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <p className="font-semibold text-slate-900" data-testid={`score-explain-headline-${rowId}`}>
          {explanation.headline}
        </p>
        {onClose ? (
          <button
            type="button"
            data-testid={`score-explain-close-${rowId}`}
            onClick={onClose}
            className="rounded border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-500 hover:bg-slate-100"
          >
            Close
          </button>
        ) : null}
      </div>

      {/* the weighted factors — they add up to the percentage above */}
      <table className="mb-3 w-full text-[12px]" data-testid={`score-explain-factors-${rowId}`}>
        <thead className="text-[10px] uppercase tracking-wider text-slate-400">
          <tr>
            <th className="py-1 pr-4 text-left font-semibold">Criterion</th>
            <th className="whitespace-nowrap py-1 pr-4 text-left font-semibold">Weight</th>
            <th className="py-1 pr-4 text-left font-semibold">Result of the check</th>
            <th className="whitespace-nowrap py-1 text-right font-semibold">Points</th>
          </tr>
        </thead>
        <tbody>
          {explanation.factors.map((factor) => {
            const style = VERDICT_STYLE[factor.verdict];
            return (
              <tr
                key={factor.key}
                data-testid={`score-explain-factor-${factor.key}`}
                data-verdict={factor.verdict}
                className="border-t border-slate-200/70"
              >
                <td className="py-1 pr-4 font-medium text-slate-800">{factor.label}</td>
                <td className="whitespace-nowrap py-1 pr-4 text-slate-500">{factor.weight}%</td>
                <td className="py-1 pr-4">
                  <span className={`rounded px-1.5 py-0.5 text-[11px] ${style.className}`}>
                    {style.label}
                  </span>
                  <span className="ml-2 text-slate-500">{factor.note}</span>
                </td>
                <td className="py-1 text-right font-semibold text-slate-800">
                  {factor.points} / {factor.weight}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t border-slate-300">
            <td className="py-1 font-semibold text-slate-700" colSpan={3}>
              Total confirmed
            </td>
            <td className="py-1 text-right font-bold text-slate-900">
              {explanation.earnedPoints} / {explanation.maxPoints}
            </td>
          </tr>
        </tfoot>
      </table>

      {explanation.unprovenNote ? (
        <p
          data-testid={`score-explain-unproven-${rowId}`}
          className="mb-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800"
        >
          <i className="fa-solid fa-circle-question mr-1" aria-hidden />
          {explanation.unprovenNote}
        </p>
      ) : null}

      {/* AC2 — why a low-percentage company is on the list at all */}
      <p className="mb-1" data-testid={`score-explain-list-reason-${rowId}`}>
        <b className="text-slate-900">Why is this company on the list?</b> {explanation.listReason}
      </p>

      {explanation.gradeNote ? (
        <p className="mb-1 text-slate-600" data-testid={`score-explain-grade-note-${rowId}`}>
          <b className="text-slate-900">Grade:</b> {explanation.gradeNote}
        </p>
      ) : null}

      {explanation.rationale ? (
        <p className="text-slate-600" data-testid={`score-explain-rationale-${rowId}`}>
          <b className="text-slate-900">Check summary:</b> {explanation.rationale}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// the panel as a table row (both long-list tables)
// ---------------------------------------------------------------------------

export function ScoreExplainDetailsRow({
  explanation,
  rowId,
  colSpan,
  onClose,
}: {
  readonly explanation: ScoreExplanation;
  readonly rowId: number;
  readonly colSpan: number;
  readonly onClose: () => void;
}): JSX.Element {
  return (
    <tr data-testid={`score-explain-row-${rowId}`} className="bg-slate-50">
      <td colSpan={colSpan} className="px-3 pb-4 pt-0">
        <ScoreExplainPanel explanation={explanation} rowId={rowId} onClose={onClose} />
      </td>
    </tr>
  );
}
